import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { Maybe } from '@/helpers/types';
import type { MaybeRefOrGetter } from '@vueuse/core';
import type { GetPackageProviderPlansQuery } from '@/features/buybox/graphql/queries/GetPackageProviderPlans.query';
import type { AdServerFtCreativeFragment } from '@/features/ad-server/graphql/fragments/AdServerFTCreative.fragment';

import { computed } from 'vue';
import { toValue } from '@vueuse/core';
import { storeToRefs } from 'pinia';

import { useGeoSwiper } from '@/features/buybox/composables/useGeoSwiper';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { useQuery } from '@/helpers/composables/useApollo';
import { getOffersForCountry } from '@/helpers/offer-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { GetPackageProviderPlansDocument } from '@/features/buybox/graphql/queries/GetPackageProviderPlans.query';

interface UseFreeTrialOptions {
	title: MaybeRefOrGetter<TitleDetail>;
	offers: MaybeRefOrGetter<TitleOfferFragment[]>;
	hardcodedCountry?: MaybeRefOrGetter<Maybe<string>>;
	hardcodedShortName?: MaybeRefOrGetter<string | undefined>;
	adServerCreative?: MaybeRefOrGetter<AdServerFtCreativeFragment | undefined>;
}

export function useFreeTrial({
	title,
	offers,
	hardcodedCountry,
	hardcodedShortName,
	adServerCreative,
}: UseFreeTrialOptions) {
	const { country, language } = storeToRefs(useLanguageStore());

	const adServerFTCreative = computed(() => {
		const creative = toValue(adServerCreative);
		return creative?.__typename === 'CreativeFreeTrialContent' ? creative : undefined;
	});

	/* Geo Swiper - fetch localised offers */
	const fetchGeoSwiperOffers = computed(() => {
		if (adServerFTCreative.value) return false;
		return !!toValue(hardcodedCountry) && !!toValue(hardcodedShortName);
	});

	const { geoSwiperOffers } = useGeoSwiper({
		title,
		enable: fetchGeoSwiperOffers,
		offerShortName: hardcodedShortName,
		offerCountry: hardcodedCountry,
	});

	const localisedOffers = computed(() => {
		if (fetchGeoSwiperOffers.value) return geoSwiperOffers.value;
		return toValue(offers);
	});

	const countryOrGeoCountry = computed(() => toValue(hardcodedCountry) ?? country.value);

	/* Generate Free Trial from config */
	const { inPromotionCatalogue, promotionOffer, catalogueItem } = usePromotion({
		title,
		offers: localisedOffers,
		hardcodedShortName,
		countryOrGeoCountry,
	});

	const isFallback = computed(() => {
		if (adServerFTCreative.value) {
			const adServerShortName = adServerFTCreative.value.offer.package.shortName;
			return !getOffersForCountry(toValue(title).offers ?? [], countryOrGeoCountry.value).some(
				o => o.package.shortName === adServerShortName
			);
			// return adServerFTCreative.value.offerSource === 'fallbackOffer';
		}
		return !inPromotionCatalogue.value;
	});

	const _promotionOffer = computed(() => {
		if (adServerFTCreative.value) return adServerFTCreative.value.offer as TitleOfferFragment;
		return promotionOffer.value;
	});

	const providerShortNames = computed(() =>
		fetchGeoSwiperOffers.value ? [toValue(hardcodedShortName)] : [catalogueItem.value?.shortName ?? '']
	);

	const isAmazonChannel = computed(
		() =>
			!!catalogueItem.value?.technicalName.startsWith('amazon') &&
			!['prv', 'amp', 'amz'].includes(catalogueItem.value?.shortName ?? '')
	);

	const enableGetPackageProviderPlans = computed(() => {
		if (adServerFTCreative.value) return false;
		return isFallback.value || fetchGeoSwiperOffers.value;
	});

	/* For Fallback FTs - fetch package provider plans */
	const { result: packageProviderPlans } = useQuery<GetPackageProviderPlansQuery>(
		GetPackageProviderPlansDocument,
		() => ({
			country: countryOrGeoCountry.value,
			language: language.value,
			packages: providerShortNames.value,
			includeAddons: isAmazonChannel.value,
		}),
		{
			enabled: enableGetPackageProviderPlans,
			errorPolicy: 'all',
			prefetch: false,
		}
	);

	const packagePlan = computed(() => {
		if (adServerFTCreative.value) return getPlanOffer(adServerFTCreative.value.offer.package);

		const mainPackage = isFallback.value
			? packageProviderPlans.value?.packages?.[0]
			: promotionOffer.value?.package;

		if (!mainPackage) return null;

		return getPlanOffer(mainPackage);
	});

	return {
		promotionOffer: _promotionOffer,
		isFallback,
		packagePlan,
		countryOrGeoCountry,
		fetchGeoSwiperOffers,
	};
}

export type PlanOffer = ReturnType<typeof getPlanOffer>;

function getPlanOffer(pckg: TitleOfferFragment['package'] | GetPackageProviderPlansQuery['packages'][0]) {
	if (pckg.planOffers.length > 0) {
		const plan = pckg.planOffers.find(offer => offer.isTrial || offer.children.length > 0);
		if (plan && plan.children.length > 0) {
			return { ...plan.children[0], retailPrice: plan.retailPrice };
		}
		return plan;
	}
	return null;
}
